// @flow
import Popup from "../COMPONENTS/Popup";
import ErrorPopup from "../COMPONENTS/ErrorPopup";

let liToken = ""; // backing field for the liToken Property
let liApiVersion = "";   // default api version (not set)

export type AuthInfoWithConflict = {
    isAuthenticated: boolean;
    tokenInfo: any;
    userId: number;
    fullName: string;
    level: string;
    conflict: boolean;  // will be true if the lockfile is held by another user
    conflictMsg: string; // the message outlining the conflict
}

class CommonClientApi {
    
    //#region HEADER
   
    static get liToken() {
        return liToken;
    }

    static set liToken(value) {
        //console.log("liToken (real) set to " + value);
        liToken = value;
    }
    
    static get liApiVersion(): string {
        return liApiVersion;
    }
    
    static set liApiVersion(value) {
        console.log("liApiVersion set to " + value);
        liApiVersion = value;
    }
    
    /** setup the header fields for communications with the webservice.
     *  The cookies are used as the source of truth during a refresh.
     * @param liToken
     * @param liApiVersion
     */
    static setupHeader(liToken, liApiVersion="") {
        CommonClientApi.liToken = liToken;
    }
    
    static setupHeaderApiVersion(liApiVersion) {
        if (liApiVersion !== "") {
            CommonClientApi.liApiVersion = liApiVersion;
        }
    }

    /* send a login request to the server */
    static sendSignInRequest(code) {

        // Description: request a login token from the server
        //
        // POST /api/auth/token
        //
        // Body: {
        //    code:  <code>
        // }
        //
        // Returns: {
        //      "tokenInfo": {
        //          token: sdlkfjsdlfkjsdEREsdfsdDfsdflkjl23kjlkjsdflksjdflksjdflksjdflskdjfsldkjflskdjf
        //          expiration: 1553710066  (unix seconds)
        //      },
        //     userId: <long or 0>
        //     level: 'PICKER,SEARCHER,etc'
        //     fullName: <string>
        // }
        //

        console.log(
            `sendSigninRequest(): sending signin request...`
        );

        return new Promise((resolve, reject) => {
            const body = {
                code: code
            };
            this.sendRequestAsJson("/api/auth/token", "POST", body, 10000)
            .then((response) => {
                if(this.checkForNoResponseOrErrorResponse(response,reject)) {
                    return;
                }
               
                const tokenInfo = Object.assign({}, response.tokenInfo);
                let isAuthenticated = false;
                if (tokenInfo.token !== "") {
                    // we got a token back -- we are logged in
                    isAuthenticated = true;
                }
                resolve({isAuthenticated, tokenInfo, userId: response.userId, fullName: response.fullName, level: response.level});
            })
            .catch((e) => {
                reject(e);
            });
        });
    }
    
    static sendSignInRequestUsingLocking(code:string,machineName:string,stage:number=0): Promise<AuthInfoWithConflict> {
        // Description: request a login token from the server but use the pre-flight
        // method first to be able to request a steal of the lock if needed
        //
        // This returns a "Conflict" HttpStatusCode if the lock is already held
        // by a different person or this same person at a different machine
        //
        // POST /api/auth/login_preflight?stage=<stage>
        //
        // Body: {
        //    code:  <code>,
        //    machineName: <machineName>
        // }
        //
        // Returns: {
        //      "tokenInfo": {
        //          token: sdlkfjsdlfkjsdEREsdfsdDfsdflkjl23kjlkjsdflksjdflksjdflksjdflskdjfsldkjflskdjf
        //          expiration: 1553710066  (unix seconds)
        //      },
        //     userId: <long or 0>
        //     level: 'PICKER,SEARCHER,etc'
        //     fullName: <string>
        // }
        //

        console.log(
            `sendSigninRequestUsingLocking(): sending signin request (using preflight)...`
        );

        return new Promise((resolve, reject) => {
            const body = {
                code: code,
                fromMachine: machineName
            };
            this.sendRequestAsJson("/api/auth/login_preflight?stage="+stage, "POST", body, 10000)
            .then((response) => {
                if (!response) {
                    reject(this.buildErrorReturn(400, "No Specific Error provided - is the Archiver Service running?"));
                    return;
                }
                if(response.statusCode === 409) { // lockfile is held by another user
                    resolve({
                        isAuthenticated: false, 
                        tokenInfo: null,
                        userId: 0,
                        fullName: "",
                        level: "",
                        conflict: true,
                        conflictMsg: response.error // the conflict message from the server
                    });
                    return;
                }
                if(this.checkForErrorResponse(response, reject)) { // any other general errors
                    return;
                }
                
                const tokenInfo = Object.assign({}, response.tokenInfo);
                let isAuthenticated = false;
                if (tokenInfo.token !== "") {
                    // we got a token back -- we are logged in
                    isAuthenticated = true;
                }
                resolve({
                    isAuthenticated, 
                    tokenInfo, 
                    userId: response.userId, 
                    fullName: response.fullName, 
                    level: response.level, 
                    conflict: false,
                    conflictMsg: ""
                });
            })
            .catch((e) => {
                reject(e);
            });
        });
    }
    
    /* send signout request to the server */
    static sendSignOutRequest(fullName:string=null) {
        // Description: sign this user out of the system
        //
        // GET /api/auth/signout?fullName=
        //
        // Body: { }
        //
        // Response:
        // {
        //    msg: user 'xxx' is signed out,
        //    isAuthenticated: false
        // }
        //

        console.log("sendSignoutRequest(): sending signoutRequest");

        return new Promise((resolve, reject) => {
            this.sendRequestAsJson(`/api/auth/signout?fullName=${fullName}`, "GET")
            .then((response) => {
                CommonClientApi.liToken = undefined;
                resolve(response);
            })
            .catch((e) => {
                reject("could not log you out [" + e + "]")
            });
        });
    }
    
    //#endregion HEADER

    //#region INFO

    static getServerInfo() {
        // Description: request some basic info back from the webservice 
        //
        // GET /api/info
        //
        // Body: {}
        //
        // Returns: {
        //     version: <str>,
        //     name: <str>,
        //     id: <number>
        //     ...
        // }
        //

        console.log(
            `getServerInfo(): sending request ...`
        );

        return new Promise((resolve, reject) => {
            this.sendRequestAsJson("/api/info", "GET")
            .then((response) => {
                if(this.checkForNoResponseOrErrorResponse(response,reject)) {
                    return;
                }
                // coming from the archiver: name, version, demoMode, requiredDbVersion, dbVersion, localTimeUtc,
                // config: { enabled: { <module>: <bool> }, inactiveTimeout: <minutes> }
                resolve({
                    name:response.name,
                    version:response.version,
                    id: response.id, 
                    demoMode: response.demoMode, 
                    dbVersion: response.dbVersion, 
                    requiredDbVersion: response.requiredDbVersion, 
                    localTimeUtc: response.localTimeUtc,
                    config: response.config});
            })
            .catch((e) => {
                reject(e);
            });
        });
    }

    static getArchiverServerInfo() {
        // Description: request some basic info back from the archiver webservice 
        //
        // GET /api/info
        //
        // Body: {}
        //
        // Returns: {
        //     version: <str>,
        //     name: <str>,
        //     demoMode: <bool>,
        //     dbVersion: <str>,
        //     requiredDbVersion: <str>
        //     ...
        // }
        //

        console.log(
            `getServerInfo(): sending request ...`
        );

        return new Promise((resolve, reject) => {
            this.sendRequestAsJson("/api/info/archiver", "GET")
            .then((response) => {
                if(this.checkForNoResponseOrErrorResponse(response,reject)) {
                    return;
                }
                resolve({name:response.name,version:response.version,demoMode:response.demoMode,
                    dbVersion:response.dbVersion, requiredDbVersion:response.requiredDbVersion,
                    localTimeUtc:response.localTimeUtc, id: response.id});
            })
            .catch((e) => {
                reject(e);
            });
        });
    }

    static getServerSideWwwAppInfo() {
        /**
         * send a request for the version of the web app that should be running right now
         *
         * Sent:  /version.html
         *
         * Returns:
         * {
         *    version: <the version string - no 'v'>
         * }
         */

        console.log(
            `getServerSideWwwAppInfo(): sending INFO request...`
        );

        return new Promise((resolve, reject) => {
            this.sendRequestForTextFile("/version.html")
            .then((versionStr) => {
                versionStr=versionStr?.replace("\n","")??"--.----.-";
                resolve({serverSideWwwAppVersion:versionStr});
            })
            .catch((e) => {
                reject(e);
            });
        });
    }

    //#endregion INFO

    /**
     * show and error popup unless the error is related to being unauthourized since the top level should handle that.
     * @param responseError
     * @param afterFunc
     * @constructor
     */
    static ShowErrorIfNotUnauthourized(responseError, afterFunc:function) {

        if (responseError?.statusCode === 401) {
            Popup.show("Unauthorized","<p><b>Your attempt to access the system has failed.</b><br/>This is either because:" +
                "<ul><li>your user code is incorrect</li><li>OR your session has expired</li></ul></p><p>Please dismiss this dialog to login again.</p>",
                () => {
                    window.location.href="/app/signout";
                });
            return;
        }
        let error = responseError?.error??"(none specified)";
        const lowerError = error?.toLowerCase()??"(none specified)";
        if(lowerError.indexOf("forbidden")!==-1) { // not enough user level
            error="Forbidden";
        }
        else if (error.indexOf("trying to proxy") !== -1) {
            error = "<p>Failed to connect to Archiver WebService.</p><p>Please have your Archiver administrator ensure that the Archiver is accessible and that its WebService is running before trying again.</p>";
        }
        ErrorPopup.showError("ERROR", error, afterFunc);
    }
    
    static getSlideImage(imageName, imageType) {
        // Description: request an image from the webservice
        //
        // GET /api/slideImage/firmware/<imageName> or 
        // GET /api/slideImage/db/<imageName>
        //
        // Body: { }
        //
        // Response:
        // {
        //    <image data>
        // }
        //

        console.log(`getSlideImage(): sending for ${imageName} of type ${imageType}`);
        
        return new Promise((resolve, reject) => {
            this.sendRequestAsJson(`/api/slideimage/${imageType}/${imageName}`, "GET")
            .then((response) => {
                if(this.checkForNoResponseOrErrorResponse(response,reject)) {
                    return;
                }
                resolve(response);
            })
            .catch((e) => reject("could get image.  Details: " + e + "]"));
        });
    }
    
    static uploadImageAsSampleImage(imageData) {
        // Description: send raw file image data up to the server
        //
        // POST /api/general/image
        //
        // Body: {
        //    imageData:  <code>
        // }
        //
        // Returns: {
        //     id: <#>
        // }
        //

        console.log(
            `uploadImageAsSampleImage(): sending upload request...`
        );

        return new Promise((resolve, reject) => {
            const body = {
                imageData: imageData
            };
            this.sendRequestAsJson("/api/general/image", "POST", body)
            .then((response) => {
                if(this.checkForNoResponseOrErrorResponse(response,reject)) {
                    return;
                }
                resolve(response.id);
            })
            .catch((e) => {
                reject(e);
            });
        });
    }
    
    //#region DEVICE_STATUS_UPDATE

    static sendDeviceStatusUpdate(status:string, mode:string) {
        // Description: send the status of the device
        //
        // POST /api/status
        //
        // Body: { 
        //   status: <str>
        //   }
        //
        // Returns: {
        //      currentUser: {
        //          name: <str>,
        //          time: <datetime>,
        //          from: <string>
        //      }
        // }
        //

        console.log(
            `sendDeviceStatusUpdate(): sending request...`
        );

        return new Promise((resolve, reject) => {
            this.sendRequestAsJson("/api/device/status", "POST", {status: status,mode: mode})
            .then((response) => {
                if(this.checkForErrorResponse(response,reject)) {
                    return;
                }
                resolve({currentUser:response?.currentUser});
            })
            .catch((e) => {
                reject(e);
            });
        });
    }
    
    //#endregion DEVICE_STATUS_UPDATE
    
    static cleanWebServiceDb() {
        // Description: request the webservice to clean the database
        //
        // POST /api/general/clean
        //
        // Body: { }
        //
        // Returns: {}
        //

        console.log(
            `cleanWebServiceDb(): sending request...`
        );

        return new Promise((resolve, reject) => {
            this.sendRequestAsJson("/api/admin/cleandb", "POST")
            .then((response) => {
                if(this.checkForNoResponseOrErrorResponse(response,reject)) {
                    return;
                }
                resolve(response);
            })
            .catch((e) => {
                reject(e);
            });
        });
    }
    
    //#region SYNC_ITEMS
    static getSyncItems() {
        // Description: request the webservice to get the sync items
        //
        // GET /api/synclog/all
        //
        // Body: { }
        //
        // Returns: {
        //    items: [ { <item> }, ... ]
        // }
        //

        console.log(
            `getSyncItems(): sending request...`
        );

        return new Promise((resolve, reject) => {
            this.sendRequestAsJson("/api/synclog/all", "GET")
            .then((response) => {
                if(this.checkForNoResponseOrErrorResponse(response,reject)) {
                    return;
                }
                resolve(response.items);
            })
            .catch((e) => {
                reject(e);
            });
        });
    }
    
    static removeSyncItem(idList: Array<number>) {
        
        // Description: request the webservice to remove a sync item
        //
        // PUT /api/synclog/remove
        //
        // Body: { 
        //  syncLogIds: [<id>,<id>,...]}
        //
        // Returns: {}
        //

        console.log(
            `removeSyncItem(): sending request...`
        );
        
        const body = {
            syncLogIds: idList
        }

        return new Promise((resolve, reject) => {
            this.sendRequestAsJson(`/api/synclog/remove`, "PUT", body)
            .then((response) => {
                if(this.checkForNoResponseOrErrorResponse(response,reject)) {
                    return;
                }
                resolve(response.numRemoved);
            })
            .catch((e) => {
                reject(e);
            });
        });
    }
    
    //#endregion SYNC_ITEMS
    
    //#region GENERAL_COMMS_HANDLERS
    static sendRequestForTextFile(url) {
        // Description: request a text file from the webservice
        //
        // GET /url
        //
        // Body: { }
        //
        // Response:
        // {
        //    <text file data>
        // }
        //

        const now = Date.now();
        console.log(`sendRequestForTextFile(): sending for ${url}`);

        const headers = new Headers();
        headers.append("Content-Type","text/html");
        headers.append("Authorization", "Bearer " + CommonClientApi.liToken);
        if(CommonClientApi.liApiVersion !== "") {
            headers.append("X-Api-Version", CommonClientApi.liApiVersion);
        }

        return fetch(url+"?"+now, {
            method: "GET", // *GET, POST, PUT, DELETE, etc.
            mode: "same-origin", // no-cors, cors, *same-origin
            cache: "no-cache", // *default, no-cache, reload, force-cache, only-if-cached
            credentials: "omit", // include, *same-origin, omit (JS credentials)
            headers: headers, 
            redirect: "error", // manual, *follow, error
            referrer: "no-referrer", // no-referrer, *client
        })
        .then( (response) => {
            if (response.headers.has("Content-Type")) {
                if (response.headers.get("Content-Type").startsWith("text/html")) {
                    return response.text();
                }
            }
            return response;
        })
        .catch((e) => {
            this.handleServerInternalError(e)
        });
    }
    
    /** send some data to the server using GET, PUT, POST or any REST action.
     * The body is sent as a JSON string.
     * NOTE: if GET is specified, the body will not be included in the message as per
     * REST requirements.
     *
     * @param url url to send the request to (plus any query string)
     * @param method one of GET, POST, PUT, DELETE (default is GET)
     * @param body body object to send (empty by default)
     * @param timeout the timeout in milliseconds (60000 by default)
     * @param abortController a pointer to the abort controller if abort is supported (passed on to fetch)
     * @returns {Promise<any | never>}
     */
    static sendRequestAsJson(url = "", method = "GET", body = {}, timeout = 60000, abortController=null) {
        let requestInit = {
            method: method, // *GET, POST, PUT, DELETE, etc.
            mode: "same-origin", // no-cors, cors, *same-origin
            cache: "no-cache", // *default, no-cache, reload, force-cache, only-if-cached
            credentials: "omit", // include, *same-origin, omit (JS credentials)
            headers: {
                "Content-Type": "application/json",
            },
            redirect: "error", // manual, *follow, error
            referrer: "no-referrer", // no-referrer, *client
        };
        
        // Use AbortSignal.timeout if available (Chrome 95+), otherwise use AbortController timeout
        // This provides compatibility with older browsers like Chrome 88
        if (typeof AbortSignal !== 'undefined' && typeof AbortSignal.timeout === 'function') {
            requestInit.signal = AbortSignal.timeout(timeout);
        } else if (abortController && abortController.signal) {
            requestInit.signal = abortController.signal;
        }
        
        const bearerToken = CommonClientApi.liToken;
        if (bearerToken && bearerToken !== "undefined") {
            // add tokens if we have them.
            requestInit.headers["Authorization"] = "Bearer " + bearerToken;
        }
        const apiVersion = CommonClientApi.liApiVersion;
        if (apiVersion && apiVersion !== "") {
            // add tokens if we have them.
            requestInit.headers["X-Api-Version"] = apiVersion;
        }

        if (method !== "GET") {
            // GET requests cannot have a body
            requestInit["body"] = JSON.stringify(body);
        }

        if (abortController && abortController.signal) {
            // OVERWRITING the signal here for the timeout
            requestInit.signal = abortController.signal;
        }

        return fetch(url, requestInit)
        .then((response) => this.handleServerResponse(response, abortController))
        .catch((e) => {
            if (e.name === 'AbortError') {
                console.log("sendRequestAsJson(): AbortError");
                return this.buildErrorReturn(499, "Request was aborted");
            }
            return this.handleServerInternalError(e);
        });
    }

    /** post a file to the server
     *
     * @param url the url to post to
     * @param fieldName the name of the field in the form with the fileInfoObject in it
     * @param fileInfoObject the field from the form that has the selected file in it
     * @param fileName
     */
    static postFileAsFormData(url = "", fieldName, fileInfoObject, fileName) {

        let formData = new FormData();
        formData.append(fieldName, fileInfoObject, fileName);
        
        let headers = new Headers();
        headers.append("Authorization", "Bearer " + CommonClientApi.liToken);
        if(CommonClientApi.liApiVersion !== "") {
            headers.append("X-Api-Version", CommonClientApi.liApiVersion);
        }
        let requestInit = {
            method: "POST",
            mode: "same-origin", // no-cors, cors, *same-origin
            cache: "no-cache", // *default, no-cache, reload, force-cache, only-if-cached
            credentials: "omit", // include, *same-origin, omit (credentials sent if same origin only)
            headers: headers,
            redirect: "error", // manual, *follow, error
            referrer: "no-referrer", // no-referrer, *client
            body: formData,
        };

        return fetch(url, requestInit)
        .then((response) => this.handleServerResponse(response))
        .catch((e) => this.handleServerInternalError(e));
    }

    /** returns a promise that handles a server's response (json, or text with errors
     *
     * @param response
     * @param abortController
     * @returns {*|void|Promise<any>}
     */
    static handleServerResponse(response, abortController=null):Promise<any> {
        if (response.headers.has("Content-Type")) {
            if (response.headers.get("Content-Type").startsWith("application/json")) {
                // convert to json
                const payload = response.json();
                if (payload.isError) {
                    return payload.isError;
                }
                return payload;
            }
            else if(response.headers.get("Content-Type").startsWith("image/")) { // image data
                return( // read the blob fully and then return the URL
                    response.blob().then( theBlob => URL.createObjectURL(theBlob))
                );  // return the image as a url
            }
            else if(response.headers.get("Content-Type").startsWith("application/octet-stream")) { // general bin data
                const disposition=response.headers.get("Content-Disposition");
                let fileNameHint = '';
                if (disposition && disposition.indexOf('attachment') !== -1) {
                    const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/, matches = filenameRegex.exec(disposition)
                    if (matches != null && matches[1]) fileNameHint = matches[1].replace(/['"]/g, '')
                }

                // read the blob fully and then return the URL
                //const blobPromise = response.blob();
                // if (abortController != null) {
                //     blobPromise.cancel = () => abortController.abort();
                // }
                return response.blob()
                .then(data => {
                    const blob = new Blob([data], {type: "application/octet-stream"});
                    const link = document.createElement('a');
                    link.href = window.URL.createObjectURL(blob);
                    link.download = fileNameHint;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                })
                .catch((e) => {
                    console.log("handleServerResponse(): AbortError");
                    return this.buildErrorReturn(499, "Request was aborted");
                });
            }
        }

        if (response.status >= 300 && response.status < 500) { // covers multiple choices
            return this.buildErrorReturn(response.status, response.statusText);
        }
        
        if (response.ok === false) {
            return response
            .text()
            .then((text) => {
                if (text.indexOf("Could not proxy") !== -1) {
                    // service is gone
                    text =
                        "<p>We apologize, but theServer appears to not be responding.</p><p>Please try your request again shortly.</p>";
                }
                return this.buildErrorReturn(response.status,text === "" ? "Server Response: " + response.statusText : text);
            })
            .catch((e) => this.handleServerInternalError(e));
        }
    }
    
    static buildErrorReturn(statusCode:number, errorStr:string) {
        return {
            isError: true,
            statusCode: statusCode,
            error: errorStr
        };
    }

    /** handle internal server errors
     *
     * @param error
     * @returns {{isError: boolean, error: *, statusCode: number}}
     */
    static handleServerInternalError(error) {
        return this.buildErrorReturn(500,error);
    }

    /**
     * check the given response for a blank response or just some error that's slipped through
     * and return a good error response.
     * @param response
     * @param reject
     * @returns {boolean}
     */
    static checkForNoResponseOrErrorResponse(response,reject:function) {
        if (!response) {
            reject(this.buildErrorReturn(400, "No Specific Error provided - is the Archiver Service running?"));
            return true; // error
        } 
        return this.checkForErrorResponse(response, reject);
    }

    /**
     * check the response for an error only.   Empty responses are ok and will not result in a rejection.
     * @param response
     * @param reject
     * @returns {boolean}
     */
    static checkForErrorResponse(response, reject:function) {
        if(response && response.isError) {
            reject(this.buildErrorReturn(response.statusCode ?? 400, response.error ?? "No Specific Error provided - is the Archiver Service running?"));
            return true;
        }
        return false; // all ok!
    }
    //#endregion GENERAL_COMMS_HANDLERS
}

export default CommonClientApi;
